import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ListBullets, Plus, Trash, FloppyDisk, X,
  ArrowsClockwise, FlowArrow, BookmarkSimple, Buildings,
  PencilSimple, ArrowsCounterClockwise, Sparkle,
} from "@phosphor-icons/react";

const TABS = [
  { key: "list", label: "Library" },
  { key: "triggers", label: "Triggers" },
  { key: "executions", label: "Executions" },
  { key: "test", label: "Test bench" },
];

const INTERACTION_TYPES = ["Complaint", "Query", "Feedback", "Appreciation"];

export default function SOPs() {
  const [tab, setTab] = useState("list");
  return (
    <div className="space-y-8" data-testid="sops-page">
      <div>
        <div className="mono-label text-orange-600 mb-2">// orchestration · standard operating procedures</div>
        <h1 className="font-display text-4xl md:text-5xl text-slate-900 tracking-tight leading-tight">
          SOPs
        </h1>
        <p className="text-slate-600 mt-2 max-w-2xl">
          Define how the assistant should handle a class of conversations — complaints,
          queries, feedback, appreciation. Most-specific SOP wins (Building → Block → Sector),
          and steps are woven into a warm reply without ever surfacing SOP names to customers.
        </p>
      </div>

      <div className="flex items-center gap-1 border-b border-slate-200" data-testid="sop-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`sop-tab-${t.key}`}
            className={`px-4 py-2.5 text-sm transition-all relative ${tab === t.key ? "text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
          >
            {t.label}
            {tab === t.key && <span className="absolute -bottom-px left-0 right-0 h-0.5 bg-orange-600" />}
          </button>
        ))}
      </div>

      {tab === "list" && <Library />}
      {tab === "triggers" && <Triggers />}
      {tab === "executions" && <Executions />}
      {tab === "test" && <TestBench />}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Library — list + create/edit
// ────────────────────────────────────────────────────────────────────────

function Library() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ interaction_type: "", sector_id: "" });
  const [editing, setEditing] = useState(null); // null | {} (new) | sop (edit)

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.interaction_type) params.set("interaction_type", filter.interaction_type);
      if (filter.sector_id) params.set("sector_id", filter.sector_id);
      const { data } = await api.get(`/admin/sops?${params}`);
      setRows(data.rows || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [filter.interaction_type, filter.sector_id]);

  const remove = async (id, name) => {
    if (!window.confirm(`Delete ${name}?`)) return;
    try { await api.delete(`/admin/sops/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Couldn't delete"); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 flex-wrap">
        <select
          value={filter.interaction_type}
          onChange={(e) => setFilter((f) => ({ ...f, interaction_type: e.target.value }))}
          className="border border-slate-200 px-3 py-2 text-sm bg-white outline-none"
          data-testid="sop-filter-interaction"
        >
          <option value="">All interaction types</option>
          {INTERACTION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <button onClick={load} className="btn-sharp btn-ghost text-xs"><ArrowsClockwise size={12}/></button>
        <div className="ml-auto" />
        <button onClick={() => setEditing({})} className="btn-sharp btn-primary text-sm" data-testid="sop-new">
          <Plus size={14}/> New SOP
        </button>
      </div>

      <div className="border border-slate-200 bg-white" data-testid="sop-list">
        {loading ? (
          <div className="p-12 text-center mono-label pulse-soft">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-sm italic text-slate-400">No SOPs yet — click <span className="font-mono">New SOP</span> to define one.</div>
        ) : (
          rows.map((s, i) => (
            <div key={s.id} className="px-5 py-4 border-b border-slate-100 last:border-0" data-testid={`sop-row-${i}`}>
              <div className="flex items-start gap-3">
                <span className={`text-[10px] font-mono px-1.5 py-0.5 shrink-0 mt-0.5 ${
                  s.interaction_type === "Complaint" ? "bg-rose-50 text-rose-700 border border-rose-200" :
                  s.interaction_type === "Appreciation" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
                  s.interaction_type === "Feedback" ? "bg-amber-50 text-amber-700 border border-amber-200" :
                  "bg-slate-100 text-slate-700 border border-slate-200"
                }`}>{s.interaction_type.toUpperCase()}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-medium text-slate-900">{s.title || s.intent}</div>
                    <span className="text-[10px] font-mono px-1.5 py-0.5 bg-slate-100 text-slate-600">{s.level}</span>
                    {!s.enabled && <span className="text-[10px] font-mono px-1.5 py-0.5 bg-slate-200 text-slate-700">disabled</span>}
                  </div>
                  <div className="font-mono text-[11px] text-orange-700 mt-0.5 truncate">{s.name}</div>
                  {s.description && <div className="text-xs text-slate-600 mt-1 line-clamp-2">{s.description}</div>}
                  <div className="mono-label mt-1.5">{(s.steps || []).length} steps · updated {fmt(s.updated_at)}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => setEditing(s)} className="text-slate-500 hover:text-orange-600 p-1" data-testid={`sop-edit-${i}`}>
                    <PencilSimple size={14}/>
                  </button>
                  <button onClick={() => remove(s.id, s.name)} className="text-slate-400 hover:text-rose-600 p-1" data-testid={`sop-del-${i}`}>
                    <Trash size={14}/>
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {editing !== null && (
        <Editor
          sop={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Editor (create / edit)
// ────────────────────────────────────────────────────────────────────────

function Editor({ sop, onClose, onSaved }) {
  const isNew = !sop;
  const [form, setForm] = useState(() => ({
    interaction_type: sop?.interaction_type || "Complaint",
    sector_id: sop?.sector_id || "",
    block_id: sop?.block_id || "",
    building_id: sop?.building_id || "",
    intent: sop?.intent || "",
    title: sop?.title || "",
    description: sop?.description || "",
    condition: sop?.condition || "",
    steps_text: (sop?.steps || []).join("\n"),
    decision_rules: sop?.decision_rules || "",
    escalation: sop?.escalation || "",
    enabled: sop ? sop.enabled : true,
  }));
  const [sectors, setSectors] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [saving, setSaving] = useState(false);

  // AI authoring panel state
  const [aiOpen, setAiOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiNotes, setAiNotes] = useState("");
  const [aiRefine, setAiRefine] = useState("");
  const [aiBrandHint, setAiBrandHint] = useState("");
  const [aiLastDraft, setAiLastDraft] = useState(null);

  useEffect(() => {
    api.get("/locations?type=SECTOR").then(({ data }) => setSectors(data));
  }, []);
  useEffect(() => {
    if (!form.sector_id) { setBlocks([]); return; }
    api.get(`/locations?type=BLOCK&parent_id=${form.sector_id}`).then(({ data }) => setBlocks(data));
  }, [form.sector_id]);
  useEffect(() => {
    if (!form.block_id) { setBuildings([]); return; }
    api.get(`/locations?type=BUILDING&parent_id=${form.block_id}`).then(({ data }) => setBuildings(data));
  }, [form.block_id]);

  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));

  // Apply an AI-generated draft into the form
  const applyDraft = (d) => {
    setAiLastDraft(d);
    upd({
      interaction_type: isNew ? (d.interaction_type || form.interaction_type) : form.interaction_type,
      intent: isNew ? (d.intent || form.intent) : form.intent,
      title: d.title || form.title,
      description: d.description || form.description,
      condition: d.condition || form.condition,
      steps_text: (d.steps || []).join("\n") || form.steps_text,
      decision_rules: d.decision_rules || form.decision_rules,
      escalation: d.escalation || form.escalation,
    });
    toast.success("AI draft applied — review and refine before saving");
  };

  const generate = async () => {
    if (!aiNotes.trim()) { toast.error("Add some notes first"); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/admin/sops/author", {
        raw_notes: aiNotes,
        interaction_type: form.interaction_type,
        brand_hint: aiBrandHint || undefined,
        intent_hint: form.intent || undefined,
      });
      applyDraft(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't generate");
    } finally {
      setAiBusy(false);
    }
  };

  const refine = async () => {
    if (!aiRefine.trim()) { toast.error("Tell the AI what to change"); return; }
    setAiBusy(true);
    try {
      const draft = aiLastDraft || {
        title: form.title, intent: form.intent, interaction_type: form.interaction_type,
        description: form.description, condition: form.condition,
        steps: form.steps_text.split("\n").map((s) => s.trim()).filter(Boolean),
        decision_rules: form.decision_rules, escalation: form.escalation,
      };
      const { data } = await api.post("/admin/sops/refine", { draft, instruction: aiRefine });
      applyDraft(data);
      setAiRefine("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't refine");
    } finally {
      setAiBusy(false);
    }
  };

  const save = async () => {
    if (!form.sector_id) { toast.error("Pick a sector"); return; }
    if (isNew && !form.intent.trim()) { toast.error("Intent required"); return; }
    setSaving(true);
    try {
      const body = {
        interaction_type: form.interaction_type,
        sector_id: form.sector_id,
        block_id: form.block_id || null,
        building_id: form.building_id || null,
        intent: form.intent.trim(),
        title: form.title.trim(),
        description: form.description.trim(),
        condition: form.condition.trim(),
        steps: form.steps_text,
        decision_rules: form.decision_rules.trim(),
        escalation: form.escalation.trim(),
        enabled: form.enabled,
      };
      if (isNew) await api.post("/admin/sops", body);
      else await api.put(`/admin/sops/${sop.id}`, body);
      toast.success(isNew ? "SOP created" : "SOP updated");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-30 bg-slate-900/50 flex justify-end" onClick={onClose} data-testid="sop-editor">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-4xl bg-white h-full overflow-y-auto border-l border-slate-200">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="mono-label text-orange-600">// {isNew ? "create sop" : "edit sop"}</div>
            <div className="font-display text-2xl mt-1 tracking-tight">{isNew ? "New SOP" : sop.title}</div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setAiOpen((o) => !o)}
              className={`btn-sharp text-sm ${aiOpen ? "btn-primary" : "btn-secondary"}`}
              data-testid="sop-ai-toggle"
            >
              <Sparkle size={14} weight="fill" /> {aiOpen ? "Hide AI" : "Create with AI"}
            </button>
            <button onClick={onClose} className="btn-sharp btn-ghost" data-testid="sop-editor-close"><X size={14}/></button>
          </div>
        </div>

        {aiOpen && (
          <div
            className="border-b border-orange-200 px-6 py-5 space-y-3"
            style={{ background: "linear-gradient(180deg,#FFF8F1 0%, #FFFFFF 100%)" }}
            data-testid="sop-ai-panel"
          >
            <div className="flex items-start gap-2">
              <Sparkle size={16} weight="fill" className="text-orange-600 mt-1 shrink-0" />
              <div>
                <div className="text-sm font-medium text-slate-900">SOP Authoring Assistant</div>
                <div className="mono-label">
                  Drop in rough notes, bullets, or a real example — I'll structure it into a clean, agent-ready SOP.
                  Then refine, review, and save.
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <Field label="Your raw notes / draft / example">
                  <textarea
                    rows={6}
                    value={aiNotes}
                    onChange={(e) => setAiNotes(e.target.value)}
                    placeholder="e.g. When customer says biscuit packet was crushed during delivery, apologise, get order ID and unboxing video, send replacement within 3 days; if they wanted it for an event escalate to senior team."
                    className="w-full border border-slate-200 px-3 py-2 text-sm bg-white outline-none resize-y"
                    data-testid="sop-ai-notes"
                  />
                </Field>
              </div>
              <div>
                <Field label="Brand context (optional)" hint="e.g. Aashirvaad, Sunfeast — helps the AI tone the SOP.">
                  <input
                    value={aiBrandHint}
                    onChange={(e) => setAiBrandHint(e.target.value)}
                    placeholder="Aashirvaad"
                    className="w-full border border-slate-200 px-3 py-2 text-sm bg-white outline-none"
                  />
                </Field>
                <button
                  onClick={generate}
                  disabled={aiBusy || !aiNotes.trim()}
                  className="w-full btn-sharp btn-primary text-sm mt-2"
                  data-testid="sop-ai-generate"
                >
                  <Sparkle size={14}/> {aiBusy ? "Authoring…" : "Generate SOP"}
                </button>
              </div>
            </div>

            {aiLastDraft && (
              <div className="pt-3 border-t border-orange-200">
                <Field label="Refine the draft" hint='e.g. "make step 4 more empathetic", "add a step to capture batch number", "shorten step 7"'>
                  <div className="flex gap-2">
                    <input
                      value={aiRefine}
                      onChange={(e) => setAiRefine(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && refine()}
                      placeholder="Tell the AI what to change…"
                      className="flex-1 border border-slate-200 px-3 py-2 text-sm bg-white outline-none"
                      data-testid="sop-ai-refine-input"
                    />
                    <button
                      onClick={refine}
                      disabled={aiBusy || !aiRefine.trim()}
                      className="btn-sharp btn-secondary text-sm shrink-0"
                      data-testid="sop-ai-refine"
                    >
                      <ArrowsCounterClockwise size={14}/> {aiBusy ? "…" : "Refine"}
                    </button>
                  </div>
                </Field>
                {aiLastDraft.expected_outcome && (
                  <div className="mt-2 text-xs text-slate-600 italic">
                    Expected outcome: {aiLastDraft.expected_outcome}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Interaction type">
              <select
                disabled={!isNew}
                value={form.interaction_type}
                onChange={(e) => upd({ interaction_type: e.target.value })}
                className="w-full border border-slate-200 px-3 py-2 text-sm bg-white outline-none"
                data-testid="sop-form-interaction"
              >
                {INTERACTION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Sector">
              <select
                disabled={!isNew}
                value={form.sector_id}
                onChange={(e) => upd({ sector_id: e.target.value, block_id: "", building_id: "" })}
                className="w-full border border-slate-200 px-3 py-2 text-sm bg-white outline-none"
                data-testid="sop-form-sector"
              >
                <option value="">— pick a sector —</option>
                {sectors.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Field>
            <Field label="Block (optional)">
              <select
                disabled={!isNew || !form.sector_id}
                value={form.block_id}
                onChange={(e) => upd({ block_id: e.target.value, building_id: "" })}
                className="w-full border border-slate-200 px-3 py-2 text-sm bg-white outline-none"
              >
                <option value="">(none — sector-level)</option>
                {blocks.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </Field>
            <Field label="Building (optional)">
              <select
                disabled={!isNew || !form.block_id}
                value={form.building_id}
                onChange={(e) => upd({ building_id: e.target.value })}
                className="w-full border border-slate-200 px-3 py-2 text-sm bg-white outline-none"
              >
                <option value="">(none)</option>
                {buildings.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Intent (used in canonical name)" required hint={isNew ? "e.g. ProductReplacement, OrderRefund, BatchExchange" : "Cannot change after creation"}>
            <input
              disabled={!isNew}
              value={form.intent}
              onChange={(e) => upd({ intent: e.target.value })}
              placeholder="ProductReplacement"
              className="w-full border border-slate-200 px-3 py-2 text-sm bg-white outline-none"
              data-testid="sop-form-intent"
            />
          </Field>
          <Field label="Title (shown to admins)">
            <input
              value={form.title}
              onChange={(e) => upd({ title: e.target.value })}
              placeholder="Aashirvaad — Product Replacement"
              className="w-full border border-slate-200 px-3 py-2 text-sm bg-white outline-none"
              data-testid="sop-form-title"
            />
          </Field>
          <Field label="Description" hint="Plain English — when this SOP applies. No length limit.">
            <textarea
              rows={5}
              value={form.description}
              onChange={(e) => upd({ description: e.target.value })}
              placeholder="One paragraph describing when this SOP fires and the customer outcome."
              className="w-full border border-slate-200 px-3 py-2 text-sm bg-white outline-none resize-y leading-relaxed"
              data-testid="sop-form-description"
            />
          </Field>
          <Field label="Trigger condition (plain English)" hint="The agent matches this against the customer's message. Leave blank for always-on.">
            <textarea
              rows={6}
              value={form.condition}
              onChange={(e) => upd({ condition: e.target.value })}
              placeholder="If the customer is reporting damage, defect, contamination, etc. and asking for replacement / refund / return…"
              className="w-full border border-slate-200 px-3 py-2 text-sm bg-white outline-none resize-y leading-relaxed"
              data-testid="sop-form-condition"
            />
          </Field>
          <Field label="Steps (one per line — long-form welcome)">
            <textarea
              rows={28}
              value={form.steps_text}
              onChange={(e) => upd({ steps_text: e.target.value })}
              placeholder={"1. Acknowledge the customer's frustration in one short empathetic line.\n2. Ask the customer to retain the product and original packaging for verification.\n3. Collect the order ID, store receipt, or invoice number.\n4. Request a clear photograph of the issue and the batch number on the pack.\n5. Confirm a free replacement will be arranged within 5–7 working days.\n6. Share the customer-care reference number 1800-419-0000 if asked.\n…"}
              className="w-full border border-slate-200 px-3 py-2 text-sm bg-white outline-none resize-y leading-relaxed font-mono"
              style={{ minHeight: 480 }}
              data-testid="sop-form-steps"
            />
            <div className="mono-label mt-1 text-slate-400">
              {form.steps_text.split("\n").map((s) => s.trim()).filter(Boolean).length} step(s)
            </div>
          </Field>
          <Field label="Decision rules (optional)" hint="Conditional logic the agent must follow within the procedure.">
            <textarea
              rows={5}
              value={form.decision_rules}
              onChange={(e) => upd({ decision_rules: e.target.value })}
              placeholder="If pack tampered → automatic replacement. If beyond 90 days → polite refusal with explanation."
              className="w-full border border-slate-200 px-3 py-2 text-sm bg-white outline-none resize-y leading-relaxed"
            />
          </Field>
          <Field label="Escalation (optional)">
            <textarea
              rows={4}
              value={form.escalation}
              onChange={(e) => upd({ escalation: e.target.value })}
              placeholder="If customer mentions hospitalisation or food-safety incident, escalate to FSSAI grievance team within 1 hour."
              className="w-full border border-slate-200 px-3 py-2 text-sm bg-white outline-none resize-y leading-relaxed"
            />
          </Field>
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.enabled} onChange={(e) => upd({ enabled: e.target.checked })} className="accent-orange-600"/>
            Enabled
          </label>

          <div className="flex items-center justify-end gap-2 pt-3 pb-2 pr-44 border-t border-slate-100 sticky bottom-0 bg-white z-20">
            <button onClick={onClose} className="btn-sharp btn-ghost text-sm">Cancel</button>
            <button onClick={save} disabled={saving} className="btn-sharp btn-primary text-sm" data-testid="sop-form-save">
              <FloppyDisk size={14}/> {saving ? "Saving…" : isNew ? "Create SOP" : "Save changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <div>
      <div className="mono-label mb-1.5">
        {label}{required && <span className="text-orange-600 ml-1">*</span>}
      </div>
      {children}
      {hint && <div className="mono-label mt-1 text-slate-400">{hint}</div>}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Triggers — plain-English rules with /SOP autocomplete
// ────────────────────────────────────────────────────────────────────────

function Triggers() {
  const [data, setData] = useState({ rows: [], sops: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} (new) | trigger (edit)

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/sop-triggers");
      setData(data || { rows: [], sops: [] });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id, name) => {
    if (!window.confirm(`Delete trigger "${name}"?`)) return;
    try { await api.delete(`/admin/sop-triggers/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Couldn't delete"); }
  };

  const toggle = async (t) => {
    try { await api.put(`/admin/sop-triggers/${t.id}`, { enabled: !t.enabled }); load(); }
    catch { toast.error("Couldn't update"); }
  };

  return (
    <div className="space-y-5" data-testid="sop-triggers">
      <div className="border border-orange-200 bg-orange-50/40 px-4 py-3 text-xs text-slate-700 leading-relaxed">
        Triggers are <strong>plain-English rules</strong> evaluated <em>before</em> brand-and-condition matching.
        When a trigger fires, its referenced SOP is invoked directly. Use <code className="font-mono px-1 bg-white border border-orange-200">/</code> inside the rule to mention an SOP from the library — pick from the dropdown to insert it as a chip.
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={load} className="btn-sharp btn-ghost text-xs" data-testid="trigger-refresh"><ArrowsClockwise size={12}/></button>
        <div className="ml-auto" />
        <button onClick={() => setEditing({})} className="btn-sharp btn-primary text-sm" data-testid="trigger-new">
          <Plus size={14}/> New trigger
        </button>
      </div>

      <div className="border border-slate-200 bg-white" data-testid="trigger-list">
        {loading ? (
          <div className="p-12 text-center mono-label pulse-soft">Loading…</div>
        ) : data.rows.length === 0 ? (
          <div className="p-12 text-center text-sm italic text-slate-400">
            No triggers yet — click <span className="font-mono">New trigger</span> to add one.
          </div>
        ) : (
          data.rows.map((t, i) => (
            <div key={t.id} className="px-5 py-4 border-b border-slate-100 last:border-0" data-testid={`trigger-row-${i}`}>
              <div className="flex items-start gap-3">
                <span className={`text-[10px] font-mono px-1.5 py-0.5 shrink-0 mt-0.5 ${
                  t.enabled ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
                  "bg-slate-100 text-slate-500 border border-slate-200"
                }`}>{t.enabled ? "ENABLED" : "DISABLED"}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-medium text-slate-900">{t.name}</div>
                    <span className="text-[10px] font-mono px-1.5 py-0.5 bg-slate-100 text-slate-600">priority {t.priority}</span>
                  </div>
                  <div className="text-sm text-slate-700 mt-1 leading-relaxed">
                    <RuleTextRender text={t.rule_text} />
                  </div>
                  <div className="font-mono text-[11px] text-orange-700 mt-1.5 truncate">→ {t.sop_name}</div>
                  {(t.keywords || []).length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {t.keywords.slice(0, 8).map((k) => (
                        <span key={k} className="text-[10px] font-mono px-1.5 py-0.5 bg-slate-50 text-slate-600 border border-slate-200">{k}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => toggle(t)} className="text-[11px] font-mono text-slate-500 hover:text-orange-600 px-1" data-testid={`trigger-toggle-${i}`}>
                    {t.enabled ? "disable" : "enable"}
                  </button>
                  <button onClick={() => setEditing(t)} className="text-slate-500 hover:text-orange-600 p-1" data-testid={`trigger-edit-${i}`}>
                    <PencilSimple size={14}/>
                  </button>
                  <button onClick={() => remove(t.id, t.name)} className="text-slate-400 hover:text-rose-600 p-1" data-testid={`trigger-del-${i}`}>
                    <Trash size={14}/>
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {editing !== null && (
        <TriggerEditor
          trigger={editing.id ? editing : null}
          sops={data.sops}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

// Render rule_text with /SOP_NAME tokens highlighted
function RuleTextRender({ text }) {
  if (!text) return null;
  const parts = String(text).split(/(\/[A-Za-z][A-Za-z0-9_]+)/g);
  return (
    <>
      {parts.map((p, i) =>
        p.startsWith("/") ? (
          <span key={i} className="inline-flex items-center font-mono text-[11px] px-1.5 py-0.5 bg-orange-50 text-orange-700 border border-orange-200 mx-0.5 align-baseline">
            {p}
          </span>
        ) : (
          <span key={i}>{p}</span>
        )
      )}
    </>
  );
}

function TriggerEditor({ trigger, sops, onClose, onSaved }) {
  const isNew = !trigger;
  const enabledSops = useMemo(() => (sops || []).filter((s) => s.enabled !== false), [sops]);

  const [form, setForm] = useState(() => ({
    name: trigger?.name || "",
    rule_text: trigger?.rule_text || "",
    sop_id: trigger?.sop_id || "",
    priority: trigger?.priority ?? 100,
    enabled: trigger ? trigger.enabled : true,
  }));
  const [saving, setSaving] = useState(false);

  // /-autocomplete state
  const taRef = React.useRef(null);
  const slashIdxRef = React.useRef(0);
  const [showSuggest, setShowSuggest] = useState(false);
  const [suggestQuery, setSuggestQuery] = useState("");
  const [suggestIdx, setSuggestIdx] = useState(0);

  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));

  // Find /word fragment immediately before cursor — show dropdown when present
  const onRuleChange = (e) => {
    const v = e.target.value;
    upd({ rule_text: v });
    const cursor = e.target.selectionStart;
    const upto = v.slice(0, cursor);
    const m = upto.match(/(?:^|[\s])\/(\w*)$/);
    if (m) {
      slashIdxRef.current = cursor - m[1].length - 1;
      setSuggestQuery(m[1]);
      setShowSuggest(true);
      setSuggestIdx(0);
    } else {
      setShowSuggest(false);
    }
  };

  const filtered = useMemo(() => {
    const q = (suggestQuery || "").toLowerCase();
    const list = enabledSops.filter((s) =>
      !q ||
      (s.name || "").toLowerCase().includes(q) ||
      (s.title || "").toLowerCase().includes(q)
    );
    return list.slice(0, 8);
  }, [enabledSops, suggestQuery]);

  const insertSop = (sop) => {
    const ta = taRef.current;
    if (!ta) return;
    const cursor = ta.selectionStart;
    const before = form.rule_text.slice(0, slashIdxRef.current);
    const after = form.rule_text.slice(cursor);
    const ins = `/${sop.name}`;
    const next = before + ins + after;
    setForm((f) => ({ ...f, rule_text: next, sop_id: sop.id }));
    setShowSuggest(false);
    setTimeout(() => {
      ta.focus();
      const newPos = before.length + ins.length;
      ta.setSelectionRange(newPos, newPos);
    }, 0);
  };

  const onKeyDown = (e) => {
    if (!showSuggest || filtered.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setSuggestIdx((i) => (i + 1) % filtered.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSuggestIdx((i) => (i - 1 + filtered.length) % filtered.length); }
    else if (e.key === "Enter") { e.preventDefault(); insertSop(filtered[suggestIdx]); }
    else if (e.key === "Escape") { setShowSuggest(false); }
  };

  // Pick referenced SOP from rule_text if user typed it manually (last /SOP_NAME mentioned wins)
  const refSop = useMemo(() => {
    if (form.sop_id) return enabledSops.find((s) => s.id === form.sop_id);
    const matches = (form.rule_text || "").match(/\/[A-Za-z][A-Za-z0-9_]+/g) || [];
    if (matches.length === 0) return null;
    const lastName = matches[matches.length - 1].slice(1);
    return enabledSops.find((s) => s.name === lastName) || null;
  }, [form.rule_text, form.sop_id, enabledSops]);

  // Keep sop_id in sync if rule_text last-mention changes and user hasn't picked manually
  useEffect(() => {
    if (refSop && refSop.id !== form.sop_id) {
      setForm((f) => ({ ...f, sop_id: refSop.id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refSop?.id]);

  const save = async () => {
    if (!form.rule_text.trim()) { toast.error("Rule text required"); return; }
    if (!form.sop_id) { toast.error("Mention an SOP via / to bind this trigger"); return; }
    setSaving(true);
    try {
      const body = {
        name: form.name.trim() || form.rule_text.trim().slice(0, 60),
        rule_text: form.rule_text.trim(),
        sop_id: form.sop_id,
        priority: Number(form.priority) || 100,
        enabled: form.enabled,
      };
      if (isNew) await api.post("/admin/sop-triggers", body);
      else await api.put(`/admin/sop-triggers/${trigger.id}`, body);
      toast.success(isNew ? "Trigger created" : "Trigger updated");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-30 bg-slate-900/50 flex justify-end" onClick={onClose} data-testid="trigger-editor">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-white h-full overflow-y-auto border-l border-slate-200">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="mono-label text-orange-600">// {isNew ? "create trigger" : "edit trigger"}</div>
            <div className="font-display text-2xl mt-1 tracking-tight">{isNew ? "New trigger" : trigger.name}</div>
          </div>
          <button onClick={onClose} className="btn-sharp btn-ghost" data-testid="trigger-editor-close"><X size={14}/></button>
        </div>

        <div className="p-6 space-y-5">
          <Field label="Name" hint="A short label for admins. If left blank, the rule text is used.">
            <input
              value={form.name}
              onChange={(e) => upd({ name: e.target.value })}
              placeholder="e.g. Damaged pack — Aashirvaad"
              className="w-full border border-slate-200 px-3 py-2 text-sm bg-white outline-none"
              data-testid="trigger-form-name"
            />
          </Field>

          <Field
            label="Rule (plain English) — type / to insert an SOP"
            required
            hint="Example: If the customer input is a complaint about damage, invoke / to choose the SOP."
          >
            <div className="relative">
              <textarea
                ref={taRef}
                rows={5}
                value={form.rule_text}
                onChange={onRuleChange}
                onKeyDown={onKeyDown}
                placeholder="If the customer reports a damaged or crushed product, invoke /"
                className="w-full border border-slate-200 px-3 py-2 text-sm bg-white outline-none resize-y leading-relaxed"
                data-testid="trigger-form-rule"
              />
              {showSuggest && (
                <div
                  className="absolute z-30 bg-white border border-slate-300 shadow-lg w-full max-w-xl mt-1"
                  data-testid="trigger-sop-suggest"
                >
                  <div className="mono-label px-3 py-2 border-b border-slate-200 bg-slate-50">
                    SOP library /{suggestQuery}
                  </div>
                  {filtered.length === 0 ? (
                    <div className="px-3 py-3 text-xs italic text-slate-400">No matching SOPs</div>
                  ) : (
                    filtered.map((s, i) => (
                      <button
                        key={s.id}
                        onMouseDown={(e) => { e.preventDefault(); insertSop(s); }}
                        onMouseEnter={() => setSuggestIdx(i)}
                        className={`block w-full text-left px-3 py-2 border-b border-slate-100 last:border-b-0 ${i === suggestIdx ? "bg-orange-50" : "hover:bg-slate-50"}`}
                        data-testid={`trigger-sop-suggest-${i}`}
                      >
                        <div className="font-mono text-xs text-orange-700 truncate">/{s.name}</div>
                        <div className="text-xs text-slate-700 truncate">{s.title || s.intent || "—"}</div>
                        <div className="mono-label text-[10px] mt-0.5">{s.interaction_type} · {s.level}</div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </Field>

          {refSop ? (
            <div className="border border-orange-200 bg-orange-50/30 px-4 py-3" data-testid="trigger-bound-sop">
              <div className="mono-label text-orange-700 mb-1">// bound SOP</div>
              <div className="font-mono text-xs text-orange-800">/{refSop.name}</div>
              <div className="text-sm text-slate-800 mt-0.5">{refSop.title || "—"}</div>
              <div className="mono-label mt-0.5">{refSop.interaction_type} · {refSop.level}</div>
            </div>
          ) : (
            <div className="border border-dashed border-slate-300 px-4 py-3 text-xs italic text-slate-400">
              Type <span className="font-mono">/</span> in the rule above to bind this trigger to an SOP.
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Priority" hint="Higher value = checked first when multiple triggers match.">
              <input
                type="number"
                value={form.priority}
                onChange={(e) => upd({ priority: e.target.value })}
                className="w-full border border-slate-200 px-3 py-2 text-sm bg-white outline-none"
                data-testid="trigger-form-priority"
              />
            </Field>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.enabled} onChange={(e) => upd({ enabled: e.target.checked })} className="accent-orange-600"/>
                Enabled
              </label>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-3 pb-2 pr-44 border-t border-slate-100 sticky bottom-0 bg-white z-20">
            <button onClick={onClose} className="btn-sharp btn-ghost text-sm">Cancel</button>
            <button onClick={save} disabled={saving} className="btn-sharp btn-primary text-sm" data-testid="trigger-form-save">
              <FloppyDisk size={14}/> {saving ? "Saving…" : isNew ? "Create trigger" : "Save changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Executions log
// ────────────────────────────────────────────────────────────────────────

function Executions() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [matchedOnly, setMatchedOnly] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/sop-executions?matched_only=${matchedOnly}`);
      setData(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [matchedOnly]);

  const totals = data?.totals || {};
  const rows = data?.rows || [];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Pill label="Evaluations" value={totals.all || 0} />
        <Pill label="SOP fired" value={totals.matched || 0} tone="orange" />
        <Pill label="Match rate" value={totals.all ? `${Math.round((100 * (totals.matched || 0)) / totals.all)}%` : "—"} />
      </div>
      <div className="flex items-center gap-2">
        <button onClick={() => setMatchedOnly(false)} className={`btn-sharp text-xs ${!matchedOnly ? "btn-primary" : "btn-secondary"}`} data-testid="sop-exec-all">All</button>
        <button onClick={() => setMatchedOnly(true)} className={`btn-sharp text-xs ${matchedOnly ? "btn-primary" : "btn-secondary"}`} data-testid="sop-exec-matched">Only matched</button>
        <button onClick={load} className="btn-sharp btn-ghost text-xs ml-auto"><ArrowsClockwise size={12}/></button>
      </div>
      <div className="border border-slate-200 bg-white" data-testid="sop-exec-list">
        {loading ? (
          <div className="p-12 text-center mono-label pulse-soft">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-sm italic text-slate-400">No SOP evaluations yet.</div>
        ) : (
          rows.map((r, i) => (
            <div key={r.id} className="px-5 py-3 border-b border-slate-100 last:border-0">
              <div className="flex items-start gap-3">
                {r.matched_sop_id ? (
                  <span className="text-[10px] font-mono px-1.5 py-0.5 bg-orange-50 text-orange-700 border border-orange-200 shrink-0 mt-0.5">
                    {r.matched_level?.toUpperCase() || "MATCH"}
                  </span>
                ) : (
                  <span className="text-[10px] font-mono px-1.5 py-0.5 bg-slate-100 text-slate-500 border border-slate-200 shrink-0 mt-0.5">NO-OP</span>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-900 truncate">{r.query}</div>
                  <div className="mono-label mt-0.5 flex flex-wrap gap-x-3">
                    <span>{r.user_email || "—"}</span>
                    <span>· intent: <span className="font-mono">{r.interaction_type}</span></span>
                    <span>· brand: <span className="font-mono">{r.brand_name || "—"}</span></span>
                    <span>· stage: <span className="font-mono">{r.stage}</span></span>
                    {r.matched_via && (
                      <span>· via: <span className={`font-mono ${r.matched_via === "trigger" ? "text-orange-700" : ""}`}>{r.matched_via}</span></span>
                    )}
                    <span>· {r.candidate_count} candidate{r.candidate_count === 1 ? "" : "s"}</span>
                    <span>· {r.latency_ms}ms</span>
                  </div>
                  {r.matched_sop_name && (
                    <div className="font-mono text-[11px] text-orange-700 mt-1 truncate">→ {r.matched_sop_name}</div>
                  )}
                  {r.trigger_rule && (
                    <div className="text-[11px] text-slate-600 mt-1 italic truncate">trigger: "{r.trigger_rule}"</div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Test bench
// ────────────────────────────────────────────────────────────────────────

function TestBench() {
  const [q, setQ] = useState("");
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const samples = useMemo(() => [
    "There's an insect in my Aashirvaad Atta pack — I want a replacement.",
    "Tell me about Aashirvaad Multigrain",
    "I love Sunfeast Dark Fantasy, please make more flavours!",
    "My Sunfeast biscuits arrived crushed.",
  ], []);
  const run = async (text) => {
    const query = (text ?? q).trim();
    if (!query) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/sops/test", { query });
      setHistory((h) => [{ query, ...data, ts: Date.now() }, ...h].slice(0, 20));
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-5" data-testid="sop-testbench">
      <div className="border border-slate-200 bg-white p-5">
        <div className="mono-label mb-3 flex items-center gap-2">
          <FlowArrow size={12} className="text-orange-600"/> Dry-run a query against the SOP library
        </div>
        <div className="flex gap-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && run()}
            placeholder="e.g. There's an insect in my Aashirvaad pack"
            className="flex-1 border border-slate-200 px-3 py-2 text-sm outline-none focus:border-orange-300"
            data-testid="sop-test-input"
          />
          <button onClick={() => run()} disabled={busy} className="btn-sharp btn-primary text-sm" data-testid="sop-test-run">
            {busy ? "…" : "Evaluate"}
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {samples.map((s) => (
            <button
              key={s}
              onClick={() => { setQ(s); run(s); }}
              className="text-xs px-2 py-1 border border-slate-200 bg-slate-50 hover:bg-orange-50 hover:border-orange-200"
            >{s}</button>
          ))}
        </div>
      </div>
      <div className="space-y-2" data-testid="sop-test-history">
        {history.length === 0 && <div className="text-sm italic text-slate-400 text-center py-6">Run a query to see how the SOP module would handle it.</div>}
        {history.map((h) => (
          <div key={h.ts} className="border border-slate-200 bg-white p-4">
            <div className="flex items-start gap-3">
              {h.matched_sop ? (
                <span className="text-[10px] font-mono px-1.5 py-0.5 bg-orange-50 text-orange-700 border border-orange-200 shrink-0 mt-0.5">
                  {h.matched_sop.level.toUpperCase()}
                </span>
              ) : (
                <span className="text-[10px] font-mono px-1.5 py-0.5 bg-slate-100 text-slate-500 border border-slate-200 shrink-0 mt-0.5">NO-OP</span>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-900">{h.query}</div>
                <div className="mono-label mt-1">
                  intent <span className="font-mono">{h.interaction_type}</span>
                  · brand <span className="font-mono">{h.brand?.name || "—"}</span>
                  · {h.candidate_count} candidate{h.candidate_count === 1 ? "" : "s"}
                  · stage <span className="font-mono">{h.stage}</span>
                  · {h.latency_ms}ms
                </div>
                {h.matched_sop && (
                  <>
                    <div className="font-mono text-[11px] text-orange-700 mt-2">{h.matched_sop.name}</div>
                    <div className="text-sm text-slate-700 mt-1">{h.matched_sop.title}</div>
                    {h.matched_sop.steps?.length > 0 && (
                      <ol className="mt-2 ml-4 text-xs text-slate-700 space-y-1 list-decimal">
                        {h.matched_sop.steps.map((s, i) => <li key={i}>{s}</li>)}
                      </ol>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Pill({ label, value, tone = "slate" }) {
  const colors = { slate: "text-slate-900", orange: "text-orange-700", emerald: "text-emerald-700" };
  return (
    <div className="border border-slate-200 bg-white p-4">
      <div className="mono-label">{label}</div>
      <div className={`font-display text-2xl mt-1 ${colors[tone] || colors.slate}`}>{value}</div>
    </div>
  );
}

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; }
}
